import {
  AnchorButton,
  Button,
  Callout,
  Card,
  Colors,
  Divider,
  Elevation,
  Intent,
  NonIdealState,
  Popover,
  PopoverInteractionKind,
  PopoverPosition,
  Tooltip
} from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import uniq from 'lodash.uniq'
import { ElementJoiner } from '../../../../../../components/ElementJoiner'
import Channels, { channelById, deprecatedChannelIds } from '../../../../../../constants/Channels'
import { usePermissions } from '../../../../../../hooks/usePermissions'
import { AppState } from '../../../../../../store'
import { UprightStyle } from '../../../../../../types'
import { hexToRGBA, rgbaForIntent } from '../../../../../../utils/color'
import { MOVEABLE_LISTING_STATES } from '../../constants'
import '../../style.scss'
import { Listing, ListingActionDialogType, ShopifyMarketAccount } from '../../types'
import { resolveAddableChannels } from '../../utils'
import { ChannelCard } from '../ChannelCard'
import ListingActionDialog from '../ListingActionDialog'
import ListingActionMenu from '../ListingActionMenu'
import { ListingInfo } from '../ListingInfo'
import { MarketAccountLite } from '../../../../v2/types'
import CreateListingButtonOrLink from '../CreateListingButtonOrLink'
import { ProductV4 } from '../../../../../../types/api'

const styles: UprightStyle = {
  arrow: {
    margin: '15px 0'
  },
  currentStepCallout: {
    paddingBottom: 15
  },
  hideStepsBtn: {
    margin: '10px 0'
  },
  spaceBetween: {
    justifyContent: 'space-between'
  },
  stepCard: {
    padding: 10,
    textAlign: 'left'
  },
  futureSteps: {
    background: hexToRGBA(Colors.GRAY3, 0.15),
    padding: '14px 12px 0 12px',
    border: `4px solid ${rgbaForIntent(Intent.PRIMARY, 0.15)}`,
    borderBottom: null,
    borderRadius: '5px 5px 0 0',
    width: '97%',
    margin: '0 auto'
  },
  pastListings: {
    background: hexToRGBA(Colors.GRAY3, 0.15),
    padding: '0 12px 14px 12px',
    border: `4px solid ${rgbaForIntent(Intent.PRIMARY, 0.15)}`,
    borderTop: null,
    borderRadius: '0 0 5px 5px',
    width: '97%',
    margin: '0 auto'
  },
  ChannelCards: {
    textAlign: 'center'
  },
  noOtherChannels: {
    paddingBottom: 20
  },
  createButtonDisabledTooltipMessage: {
    maxWidth: 300
  }
}

interface Props {
  product: ProductV4
  canonListings: Listing[]
  activeListings: Listing[]
  variantsPriceRange?: number[]
  shopifyMarketAccounts: ShopifyMarketAccount[]
  supplier?: { marketAccounts: MarketAccountLite[] }
  productPurged?: boolean
  productRecycled?: boolean
  reload: (setLoadingIndicator?: boolean) => void
  onMove: (marketId: number, marketAccountId: number) => void
  openCreateListingWhichAccountDialog: (marketId: number, additionalQueryParams: {}) => void
}

export const SingularListingControls: React.FC<Props> = props => {
  const { permissions } = usePermissions()

  const activeMarkets = React.useMemo(
    () => props.activeListings.reduce((acc, listing) => ({ [listing.marketId]: true, ...acc }), {}),
    [props.activeListings]
  )

  // which markets are not currently active, but have listings filled out/have been listed before
  const inactiveCanons = React.useMemo(
    () =>
      props.canonListings.filter(
        listing => !listing.active && !activeMarkets[listing.marketId] && listing.marketId !== Channels.OFFER_UP.id
      ),
    [props.canonListings, activeMarkets]
  )

  // given canon listings (most recent listing on each market), which markets are listable (have listings filled out)
  const availableChannelIds: number[] = React.useMemo(
    () => Array.from(props.canonListings.reduce((set, curr) => set.add(curr.marketId), new Set<number>())),
    [props.canonListings]
  )

  const connectedMarkets = useSelector((s: AppState) => {
    return s.current.organization.connectedMarkets
  })

  // channels that the organization has available
  const channelIds = connectedMarkets
    // Filtering by onboarded: see comment above "uniqueConnectedMarkets" in
    // client/routes/product_manager/components/ListingHistorySection/components/ConcurrentListingControls/index.tsx
    .filter(m => m.onboarded && m.id !== Channels.BLUEBOX.id && m.id !== Channels.OFFER_UP.id)
    .map(channel => channel.id)
  const onboardedChannels = uniq(channelIds)

  // find available channels that can be listed to, that do not have listings already
  const addableChannels = React.useMemo(
    () => resolveAddableChannels(onboardedChannels, availableChannelIds),
    [onboardedChannels, availableChannelIds]
  )

  // can the product be relisted to another market, given it's current listing state
  const isMoveable = React.useMemo(
    () => props.canonListings.every(listing => MOVEABLE_LISTING_STATES.includes(listing.state)),
    [props.canonListings]
  )

  const [selectedDialogAction, setSelectedDialogAction] = React.useState<ListingActionDialogType>({
    actionType: undefined,
    listing: undefined
  })

  const channelDisplayName = (listing, channel) => {
    return listing.accFullHumanReadableName || channel.humanReadableName
  }

  // TODO(product_inventory_unit): Improve disabled state when implementing product inventory tracking for all marketplaces and/or lister books
  const unitTrackingEnabled = !!props.product?.unitTracking
  const unitTrackingButtonDisabledMessage =
    'Cannot create a listing with unit-tracking enabled, please use the Books and ISBN Media listing process instead'

  return (
    <Card elevation={Elevation.THREE} style={styles.stepCard}>
      <ListingActionDialog
        {...selectedDialogAction}
        product={props.product}
        productRecycled={props.productRecycled}
        // TODO(shopify): - When we allow multiple shops, will need to revisit this
        marketAccountId={selectedDialogAction.listing?.marketAccountId}
        onClose={() => {
          setSelectedDialogAction({ actionType: undefined, listing: undefined })
        }}
        reloadProduct={props.reload}
      />
      {props.activeListings.length > 0 && (
        <div style={styles.currentStepCallout}>
          <Callout title="Active listings" icon={IconNames.PIVOT} />
        </div>
      )}
      <ElementJoiner separator={<Divider />}>
        {/* Show active listings for this product, regardless of listing state */}
        {props.activeListings.map((listing: Listing) => {
          const channel = channelById(listing.marketId)
          const isShopify = listing.marketId === Channels.SHOPIFY.id
          const isEbay = listing.marketId === Channels.EBAY.id
          const channelDeprecated = deprecatedChannelIds.includes(listing.marketId)
          return (
            <ChannelCard channelId={listing.marketId} key={listing.marketId}>
              <div>
                <div style={styles.channelName}>
                  <b>{channelDisplayName(listing, channel)}</b>
                </div>
                <ListingInfo
                  listing={listing}
                  hideViews={isShopify}
                  hideClickThroughRateAndImpressions={!isEbay}
                  variantsPriceRange={props.variantsPriceRange}
                  hasPriceOverride={isShopify && !!props.product.overriddenValues}
                />
              </div>
              <div style={{ display: 'flex' }}>
                {!channelDeprecated && permissions.includes('products.post') && (
                  <Link to={`/listings/${channel.name}/${listing.listingId}/edit`}>
                    <Button text="Edit" minimal intent={Intent.PRIMARY} />
                  </Link>
                )}
                {channelDeprecated && permissions.includes('products.post') && (
                  <Tooltip content="Channel no longer available">
                    <AnchorButton disabled text="Edit" minimal intent={Intent.PRIMARY} />
                  </Tooltip>
                )}
                {channelDeprecated && permissions.includes('listings.manage') && (
                  <Tooltip content="Channel no longer available">
                    <AnchorButton disabled minimal icon={IconNames.MORE} />
                  </Tooltip>
                )}
                {!channelDeprecated && permissions.includes('listings.manage') && (
                  <Popover
                    content={
                      <ListingActionMenu
                        productPurged={props.productPurged}
                        channel={channel}
                        listing={listing}
                        onActionChange={setSelectedDialogAction}
                      />
                    }
                    interactionKind={PopoverInteractionKind.HOVER}
                    position={PopoverPosition.BOTTOM_RIGHT}
                    fill
                  >
                    <Button minimal icon={IconNames.MORE} />
                  </Popover>
                )}
              </div>
            </ChannelCard>
          )
        })}
      </ElementJoiner>
      <div style={styles.currentStepCallout}>
        <Callout title="Available channels" icon={IconNames.LAYERS} />
      </div>
      <ElementJoiner separator={<Divider />}>
        {/* Show base listings for each available market */}
        {inactiveCanons.map((listing: Listing) => {
          const channel = channelById(listing.marketId)
          const humanReadableName = listing.accFullHumanReadableName || channel.humanReadableName

          const createButtonDisabled = !isMoveable || unitTrackingEnabled

          // TODO(product_inventory_unit): Improve disabled state when implementing product inventory tracking for all marketplaces and/or lister books
          let createButtonDisabledMessage = null
          if (createButtonDisabled) {
            if (!isMoveable) {
              createButtonDisabledMessage = `Cannot move to ${humanReadableName} while other postings are active`
            } else if (unitTrackingEnabled) {
              createButtonDisabledMessage = unitTrackingButtonDisabledMessage
            }
          }

          return (
            <ChannelCard channelId={listing.marketId} key={`available-listing-${listing.marketId}`}>
              <b style={{ alignSelf: 'center' }}>{humanReadableName}</b>
              <div style={{ alignSelf: 'center', display: 'flex' }}>
                {channel.name !== 'offer_up' && permissions.includes('products.post') && (
                  <Link to={`/listings/${channel.name}/${listing.listingId}/edit`}>
                    <Button text="Edit" minimal intent={Intent.PRIMARY} />
                  </Link>
                )}
                {channel.name === 'offer_up' && permissions.includes('products.post') && (
                  <Tooltip content="Channel no longer available">
                    <AnchorButton disabled text="Edit" minimal intent={Intent.PRIMARY} />
                  </Tooltip>
                )}
                {permissions.includes('listings.manage') && (
                  <Tooltip
                    disabled={!createButtonDisabled}
                    content={<div style={styles.createButtonDisabledTooltipMessage}>{createButtonDisabledMessage}</div>}
                  >
                    <Button
                      text="List"
                      style={{ marginLeft: 4 }}
                      disabled={createButtonDisabled}
                      onClick={() => props.onMove(listing.marketId, listing.marketAccountId)}
                      intent={Intent.SUCCESS}
                    />
                  </Tooltip>
                )}
              </div>
            </ChannelCard>
          )
        })}
        {/* Show additional channels that can also be listed to, but have no listings filled out */}
        {permissions.includes('products.post') &&
          addableChannels.map(channel => {
            const createButtonDisabled = props.product.overriddenValues || unitTrackingEnabled

            // TODO(product_inventory_unit): Improve disabled state when implementing product inventory tracking for all marketplaces and/or lister books
            let createButtonDisabledMessage = null
            if (createButtonDisabled) {
              if (props.product.overriddenValues) {
                createButtonDisabledMessage = 'A price override is active, remove it to create a new listing'
              } else if (unitTrackingEnabled) {
                createButtonDisabledMessage = unitTrackingButtonDisabledMessage
              }
            }

            return (
              <ChannelCard channelId={channel.id} key={channel.id}>
                <b style={{ alignSelf: 'center' }}>{channel.humanReadableName}</b>
                {!createButtonDisabled ? (
                  <CreateListingButtonOrLink
                    channel={channel}
                    connectedMarkets={connectedMarkets}
                    supplier={props.supplier}
                    productId={props.product.id}
                    openCreateListingWhichAccountDialog={props.openCreateListingWhichAccountDialog}
                  />
                ) : (
                  <Tooltip
                    disabled={!createButtonDisabled}
                    content={<div style={styles.createButtonDisabledTooltipMessage}>{createButtonDisabledMessage}</div>}
                  >
                    <Button text="Create listing" minimal intent={Intent.PRIMARY} disabled />
                  </Tooltip>
                )}
              </ChannelCard>
            )
          })}
        {permissions.includes('settings.channels') && inactiveCanons.length === 0 && onboardedChannels.length <= 1 && (
          <div style={styles.noOtherChannels}>
            <NonIdealState
              icon={IconNames.FORM}
              title="No other channels configured yet."
              description={
                <>
                  <span>You haven&apos;t configured any other channels for listing your products. Click </span>
                  <Link to="/admin/settings">here</Link>
                  <span> to configure a new channel.</span>
                </>
              }
            />
          </div>
        )}
      </ElementJoiner>
    </Card>
  )
}
